<ng-template #loadingIndicatorTemplate>
  <div
    data-test-id="loadingLayer"
    class="absolute top-0 left-0 size-full h-[calc(--spacing(4)+30vh)]"
  >
    <svg-icon
      src="assets/icons/General/loading-01.svg"
      class="absolute top-1/2 left-1/2 -m-3 size-8 animate-spin"
    />
    {{ 'forms.select.empty' | translate }}
  </div>
</ng-template>

<ng-template #noResultTemplate>
  @if (!isLoading) {
    <div
      data-test-id="noResult"
      class="m-4 flex flex-col items-center gap-4 text-lg"
    >
      <svg-icon class="size-12" src="assets/icons/Communication/inbox-01.svg" />
      {{ customNoResultText ?? ('forms.select.empty' | translate) }}
    </div>
  }
</ng-template>

<ng-template #totalTemplate let-total let-range="range"
  ><strong>{{ range[0] }}-{{ range[1] }}</strong>
  {{ 'components.table.pagination.from' | translate }} {{ total }}
</ng-template>

<ng-template #itemTemplate let-type let-page="page">
  @switch (type) {
    @case ('page') {
      <app-button
        data-test-id="pageButton"
        type="outline"
        size="small"
        class="font-medium"
      >
        {{ page }}
      </app-button>
    }
    @case ('prev') {
      <app-button
        data-test-id="prevButton"
        type="outline"
        size="small"
        class="font-medium"
      >
        <svg-icon
          src="assets/icons/Arrows/arrow-narrow-left.svg"
          class="size-4"
        />
        {{ 'components.table.pagination.back' | translate }}
      </app-button>
    }
    @case ('next') {
      <app-button
        data-test-id="nextButton"
        type="outline"
        size="small"
        class="font-medium"
      >
        {{ 'components.table.pagination.more' | translate }}
        <svg-icon
          src="assets/icons/Arrows/arrow-narrow-right.svg"
          class="size-4"
        />
      </app-button>
    }
    @default {
      <app-button
        data-test-id="middleButton"
        type="outline"
        size="small"
        class="font-medium"
      >
        ...
      </app-button>
    }
  }
</ng-template>

<nz-table
  #dynamicTable
  nzSize="small"
  data-test-id="app-table"
  nzPaginationPosition="bottom"
  nzPaginationType="small"
  [nzShowPagination]="true"
  [nzShowTotal]="totalTemplate"
  [nzItemRender]="itemTemplate"
  [nzShowSizeChanger]="false"
  [nzSimple]="false"
  [nzLoading]="isLoading"
  [nzFrontPagination]="false"
  [nzData]="tableData"
  [nzTotal]="total"
  [nzPageSize]="pageSize"
  [nzPageIndex]="pageIndex"
  [nzLoadingIndicator]="loadingIndicatorTemplate"
  [nzNoResult]="noResultTemplate"
  [nzScroll]="{ x: '1100px' }"
  (nzQueryParams)="onTableSortChange($event)"
>
  <thead>
    <tr>
      @for (tableColumn of tableColumns; track tableColumn.key) {
        <th
          #cell
          scope="col"
          class="group overflow-hidden"
          [nzWidth]="tableColumn.width ?? 'auto'"
          [nzAlign]="tableColumn.align ?? 'left'"
          [nzSortFn]="true"
          [nzShowSort]="tableColumn.sort ?? true"
          [nzColumnKey]="tableColumn.key"
          [nzSortOrder]="_defaultSorting[tableColumn.key]"
          [nzCellControl]="tableColumn.key"
          [nzLeft]="tableColumn.fixLeft ?? false"
          [nzRight]="tableColumn.fixRight ?? false"
        >
          <div class="flex items-center gap-2">
            <span class="grow" [title]="tableColumn.label | label | async">{{
              tableColumn.label | label | async
            }}</span>

            @if (tableColumn.sort !== false) {
              <span
                class="flex size-6 items-center justify-center rounded-full group-hover:text-grey"
                [title]="
                  !cell.sortOrder
                    ? ('components.table.sort.ascend' | translate)
                    : cell.sortOrder === 'ascend'
                      ? ('components.table.sort.descend' | translate)
                      : ('components.table.sort.reset' | translate)
                "
                [attr.data-test-id]="`sortButton-${tableColumn.key}`"
                [ngClass]="{
                  'text-grey-300 group-hover:bg-blue-50': !cell.sortOrder,
                  'rotate-180': cell.sortOrder === 'ascend',
                  '': cell.sortOrder === 'descend',
                  'bg-blue-100 text-grey group-hover:bg-blue-200':
                    cell.sortOrder,
                }"
                ><svg-icon
                  class="size-4"
                  src="assets/icons/Arrows/arrow-narrow-down.svg"
              /></span>
            }
          </div>
        </th>
      }
    </tr>
  </thead>
  <tbody>
    @for (data of dynamicTable.data; track data.id; let index = $index) {
      <tr>
        @for (tableColumn of tableColumns; track tableColumn.key) {
          <td
            [class]="tableColumn.class ?? null"
            [attr.title]="tableColumn.title ?? null"
            [nzAlign]="tableColumn.align ?? null"
            [nzCellControl]="tableColumn.key"
            [nzLeft]="tableColumn.fixLeft ?? false"
            [nzRight]="tableColumn.fixRight ?? false"
          >
            @if (tableColumn.cellTemplate) {
              <ng-container
                [ngTemplateOutlet]="tableColumn.cellTemplate"
                [ngTemplateOutletContext]="{
                  index: index,
                  currentRow: data,
                  item: data[tableColumn.key],
                }"
              />
            } @else {
              {{ data[tableColumn.key] }}
            }
          </td>
        }
      </tr>
    }
  </tbody>
</nz-table>
